import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { TranslatePipe } from '../../filters/translate.pipe';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import DictionaryService from '../../services/dictionary.service';
import RoutingService from '../../services/routing.service';
import ToolkitService from '../../services/toolkit.service';
import Toast from '../../services/toast.service';
import { localizeToolkitContent } from '../../utils/toolkit-content-localization.util';
import { parseJsonLenient } from '../../utils/toolkit-json.util';

@Component({
	standalone: true,
	selector: 'toolkit-preview',
	templateUrl: './toolkit-preview.component.html',
	imports: [CommonModule, FormsModule, TranslatePipe, LoaderOverlayComponent]
})
export default class ToolkitPreviewComponent implements OnInit {
	private routingService: RoutingService = inject(RoutingService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private route: ActivatedRoute = inject(ActivatedRoute);
	private toolkitService: ToolkitService = inject(ToolkitService);
	private toast: Toast = inject(Toast);
	private dictionaryService: DictionaryService = inject(DictionaryService);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.routingService.validateAccess(window.location.pathname.indexOf('/my-exercises') === 0 ? 'myExercises' : 'toolkit').then(() => {
			this.onResolve();
		});
	}

	onResolve() {
		this.vm.loading = true;
		this.vm.error = false;

		const rawKind = (this.route.snapshot.paramMap.get('kind') || 'resource').toString();
		// Backwards/compat: allow kind to be a resource format (exercise/worksheet/planner/...) and treat it as "resource".
		this.vm.kind = rawKind === 'assessment' ? 'assessment' : 'resource';
		this.vm.itemId = (this.route.snapshot.paramMap.get('id') || '').toString();
		this.vm.isPreview = this.route.snapshot.queryParamMap.get('preview') !== '0';
		this.vm.assignmentId = this.route.snapshot.queryParamMap.get('assignment_id');
		this.vm.clientId = this.route.snapshot.queryParamMap.get('client_id');

		this.vm.item = null;
		this.vm.themes = [];
		this.vm.theme = null;
		this.vm.content = null;
		this.vm.snapshotContent = null;
		this.vm.contentParseError = false;
		this.vm.assignment = null;
		this.vm.assignmentNote = null;
		this.vm.isReadOnly = false;
		this.vm.blocks = [];
		this.vm.submitting = false;
		this.vm.response = {};
		this.vm.resourceFormatLabel = this.resourceFormatLabel.bind(this);
		this.vm.difficultyLabel = this.difficultyLabel.bind(this);
		this.vm.selectChip = this.selectChip.bind(this);
		this.vm.isChipSelected = this.isChipSelected.bind(this);

		this.vm.close = this.close.bind(this);
		this.vm.submit = this.submit.bind(this);

		if (!this.vm.itemId) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		this.toolkitService.getCatalogue({}, (catalogue) => {
			this.vm.themes = Array.isArray(catalogue?.themes) ? catalogue.themes : [];
			this.attachTheme();
			this.buildBlocks();
		});

		// Optional: load assignment context to show therapist note and to prefer frozen snapshot content.
		// For client-mode (preview=0) we must use /me/assignments/{id}. For therapist preview we can use history list.
		if (this.vm.assignmentId) {
			if (!this.vm.isPreview) {
				this.toolkitService.getMyAssignment({ id: this.vm.assignmentId }, (a) => {
					this.vm.assignment = a || null;
					this.vm.assignmentNote = a?.note || null;
					const completions = Array.isArray(a?.completions) ? a.completions : [];
					this.vm.isReadOnly = completions.length > 0;
					if (a?.resourceContentSnapshotJson) {
						try {
							this.vm.snapshotContent = this.parseJsonLenient(a.resourceContentSnapshotJson);
						} catch {}
					}
					this.parseContent();
					this.buildBlocks();
				});
			} else if (this.vm.clientId) {
				this.toolkitService.getAssignments({ client_id: this.vm.clientId }, (items) => {
					const list: any[] = Array.isArray(items) ? items : [];
					const a = list.find((x) => x && (x.id?.toString() === this.vm.assignmentId?.toString()));
					this.vm.assignment = a || null;
					this.vm.assignmentNote = a?.note || null;
					// Provider preview should always be read-only and should show submitted answers (latest completion).
					this.vm.isReadOnly = true;
					const completions = Array.isArray(a?.completions) ? a.completions : [];
					this.preloadResponseFromCompletions(completions);
					if (a?.resourceContentSnapshotJson) {
						try {
							this.vm.snapshotContent = this.parseJsonLenient(a.resourceContentSnapshotJson);
						} catch {}
					}
					this.parseContent();
					this.buildBlocks();
				});
			}
		}

		const onItemLoaded = (item) => {
			this.vm.item = item;
			this.attachTheme();
			this.parseContent();
			this.buildBlocks();
			this.vm.loading = false;
		};
		const onItemError = () => {
			this.vm.loading = false;
			this.vm.error = true;
		};

		if (this.vm.kind === 'assessment') {
			this.toolkitService.getAssessment({ id: this.vm.itemId }, onItemLoaded, onItemError);
		} else {
			this.toolkitService.getResource({ id: this.vm.itemId }, onItemLoaded, onItemError);
		}
	}

	private attachTheme() {
		if (!this.vm.item || !Array.isArray(this.vm.themes)) return;
		const themeId = this.vm.item?.themeId || this.vm.item?.theme_id || this.vm.item?.theme;
		this.vm.theme = this.vm.themes.find((t) => (t?.id || t?.themeId) === themeId) || null;
	}

	resourceFormatLabel(format: string) {
		const key = (format || '').toString().trim().toLowerCase();
		switch (key) {
			case 'psychoeducation':
				return 'Toolkit.Format.Psychoeducation';
			case 'worksheet':
				return 'Toolkit.Format.Worksheet';
			case 'check-in':
			case 'checkin':
				return 'Toolkit.Format.CheckIn';
			case 'planner':
				return 'Toolkit.Format.Planner';
			case 'exercise':
			default:
				return 'Toolkit.Format.Exercise';
		}
	}

	difficultyLabel(difficulty: string) {
		const key = (difficulty || '').toString().trim().toLowerCase();
		switch (key) {
			case 'easy':
				return 'Toolkit.Difficulty.Easy';
			case 'medium':
				return 'Toolkit.Difficulty.Medium';
			case 'hard':
				return 'Toolkit.Difficulty.Hard';
			default:
				return difficulty || '';
		}
	}

	private parseContent() {
		if (this.vm.kind === 'assessment') {
			// For now, show JSON blob as preview for surveys.
			this.vm.content = null;
			return;
		}

		if (this.vm.snapshotContent && typeof this.vm.snapshotContent === 'object') {
			const localizedSnapshot = localizeToolkitContent(this.vm.snapshotContent, this.currentLocale());
			this.vm.content = this.isDynamicFormContent(localizedSnapshot)
				? this.parseDynamicFormContent(localizedSnapshot)
				: localizedSnapshot;
			this.reconcileCompletedChoiceResponsesWithLocalizedOptions();
			return;
		}

		const raw = this.vm.item?.contentJson || this.vm.item?.content_json || null;
		if (!raw) {
			this.vm.content = null;
			return;
		}
		// First try lenient JSON parse for any content type (psychoeducation sections/reflections can contain quotes).
		const anyObj = this.parseJsonLenient(raw);
		if (anyObj) {
			// If it's a dynamic form, normalize it; otherwise keep the original object (sections/reflections/etc).
			const localized = localizeToolkitContent(anyObj, this.currentLocale());
			this.vm.content = this.isDynamicFormContent(localized) ? this.parseDynamicFormContent(localized) : localized;
			this.reconcileCompletedChoiceResponsesWithLocalizedOptions();
			return;
		}

		this.vm.content = null;
		this.vm.contentParseError = true;
	}

	// Content type detection: dynamic form content is JSON with a top-level "fields" array.
	isDynamicFormContent(content: any): boolean {
		return !!(content && typeof content === 'object' && Array.isArray((content as any).fields));
	}

	// Generic lenient JSON parse used for all toolkit content payloads.
	// Handles escaped quotes, double-encoded JSON, and common "broken JSON" where quotes inside strings are not escaped.
	private parseJsonLenient(raw: any): any | null {
		return parseJsonLenient(raw);
	}

	// Parser that safely handles:
	// - already-parsed objects
	// - escaped quotes (&quot;, &#34;, &#39;)
	// - double-encoded JSON strings
	// - strings that contain a JSON object substring
	parseDynamicFormContent(raw: any): any | null {
		try {
			const parsed = localizeToolkitContent(this.parseJsonLenient(raw), this.currentLocale());
			if (!this.isDynamicFormContent(parsed)) return null;

			// Normalize minimal shape
			return {
				instructions: (parsed.instructions || '').toString(),
				fields: Array.isArray(parsed.fields) ? parsed.fields : []
			};
		} catch (e) {
			return null;
		}
	}

	private normalizeOptions(options: any[]): string[] {
		const out: string[] = [];
		(options || []).forEach((o) => {
			if (o == null) return;
			if (typeof o === 'string' || typeof o === 'number' || typeof o === 'boolean') {
				out.push(o.toString());
				return;
			}
			// Backend validator allows object options by matching submitted value against option.label.
			if (typeof o === 'object' && (o as any).label != null) {
				out.push((o as any).label.toString());
				return;
			}
			out.push(o.toString());
		});
		return out;
	}

	private optionLabelForLocale(option: any, locale: string, shouldLocalize: boolean): string {
		if (option == null) return '';
		if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
			return option.toString();
		}
		const localized = shouldLocalize ? localizeToolkitContent(option, locale) : option;
		return (
			localized?.label ||
			localized?.text ||
			localized?.title ||
			localized?.name ||
			option?.label ||
			option?.text ||
			option?.defaultText ||
			option?.value ||
			''
		).toString();
	}

	private optionStableKey(option: any): string {
		if (option == null) return '';
		if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
			return option.toString();
		}
		return (
			option?.value ??
			option?.id ??
			option?.key ??
			option?.code ??
			option?.name ??
			option?.defaultText ??
			option?.label ??
			option?.text ??
			''
		).toString();
	}

	private describeOptions(
		options: any[],
		locale: string,
		shouldLocalize: boolean
	): Array<{ label: string; key: string }> {
		return (options || []).map((option) => ({
			label: this.optionLabelForLocale(option, locale, shouldLocalize),
			key: this.optionStableKey(option)
		}));
	}

	private chipOption(option: any): { label: string; hint: string; key: string } {
		if (option == null) return { label: '', hint: '', key: '' };
		if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
			const value = option.toString();
			return { label: value, hint: '', key: value };
		}
		const localized = localizeToolkitContent(option, this.currentLocale());
		return {
			label: (
				localized?.label ||
				localized?.text ||
				option?.label ||
				option?.text ||
				option?.value ||
				''
			).toString(),
			hint: (
				localized?.hint ||
				localized?.description ||
				option?.hint ||
				option?.description ||
				''
			).toString(),
			key: this.optionStableKey(option)
		};
	}

	private findFieldById(fields: any[], fieldId: string): any | null {
		if (!Array.isArray(fields) || !fieldId) return null;
		return fields.find((field) => field && (field.id || field.key) === fieldId) || null;
	}

	private reconcileCompletedChoiceResponsesWithLocalizedOptions() {
		const response = this.vm.response;
		const currentFields = Array.isArray(this.vm.content?.fields) ? this.vm.content.fields : [];
		const snapshotFields = Array.isArray(this.vm.snapshotContent?.fields) ? this.vm.snapshotContent.fields : [];
		if (!response || typeof response !== 'object' || !currentFields.length || !snapshotFields.length) return;

		currentFields.forEach((field) => {
			if (!field) return;
			const fieldType = (field.type || '').toString();
			if (fieldType !== 'select' && fieldType !== 'chips') return;
			const fieldId = field.id || field.key;
			if (!fieldId) return;

			const savedValue = response[fieldId];
			if (savedValue === undefined || savedValue === null || savedValue === '') return;

			const currentOptions = this.describeOptions(Array.isArray(field.options) ? field.options : [], this.currentLocale(), true);
			const savedValueString = savedValue.toString();
			if (currentOptions.some((option) => option.label === savedValueString || option.key === savedValueString)) return;

			const snapshotField = this.findFieldById(snapshotFields, fieldId);
			if (!snapshotField) return;

			const snapshotOptions = this.describeOptions(
				Array.isArray(snapshotField.options) ? snapshotField.options : [],
				this.currentLocale(),
				false
			);
			const matchedIndex = snapshotOptions.findIndex((option) => option.label === savedValueString || option.key === savedValueString);
			if (matchedIndex < 0 || matchedIndex >= currentOptions.length) return;

			response[fieldId] = currentOptions[matchedIndex].label;
		});
	}

	renderFieldByType(field: any): { kind: string; placeholder?: string; options?: string[]; chipOptions?: { label: string; hint: string; key: string }[] } {
		const t = (field?.type || 'text').toString();
		if (t === 'subheader') return { kind: 'subheader' };
		if (t === 'select') return { kind: 'select', options: this.normalizeOptions(Array.isArray(field?.options) ? field.options : []) };
		if (t === 'chips') {
			const rawOptions = Array.isArray(field?.options) ? field.options : [];
			return { kind: 'chips', chipOptions: rawOptions.map((option) => this.chipOption(option)) };
		}
		// Future-safe: default to text input.
		return { kind: t, placeholder: field?.placeholder || '' };
	}

	fieldMidValue(field: any): number {
		const min = Number(field?.min ?? 0);
		const max = Number(field?.max ?? 10);
		if (!isFinite(min) || !isFinite(max)) return 0;
		return Math.round((min + max) / 2);
	}

	selectChip(fieldId: string, option: any, event?: Event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		if (this.vm.isPreview || this.vm.isReadOnly || !fieldId) return;
		if (!this.vm.response || typeof this.vm.response !== 'object') {
			this.vm.response = {};
		}
		this.vm.response[fieldId] = (option?.key ?? option?.label ?? option ?? '').toString();
	}

	isChipSelected(fieldId: string, option: any): boolean {
		if (!fieldId || !this.vm.response) return false;
		const selected = (this.vm.response[fieldId] || '').toString().trim();
		const label = (option?.label ?? option ?? '').toString().trim();
		const key = (option?.key ?? '').toString().trim();
		return selected === label || (!!key && selected === key);
	}

	private preloadResponseFromCompletions(completions: any[]) {
		if (!Array.isArray(completions) || completions.length === 0) return;
		const sorted = completions
			.slice()
			.sort((x, y) => {
				const tx = x?.submittedAt ? new Date(x.submittedAt).getTime() : x?.created ? new Date(x.created).getTime() : 0;
				const ty = y?.submittedAt ? new Date(y.submittedAt).getTime() : y?.created ? new Date(y.created).getTime() : 0;
				return ty - tx;
			});
		const latest = sorted[0];
		const raw = latest?.responseJson || latest?.response_json || null;
		if (!raw) return;
		try {
			const parsed = this.parseJsonLenient(raw);
			if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
				this.vm.response = { ...(this.vm.response || {}), ...(parsed as any) };
			}
		} catch {}
	}

	private buildBlocks() {
		if (!this.vm.item) return;
		// Initialize defaults for interactive fields (range midpoints etc)
		if (this.vm.kind === 'resource' && this.vm.content && this.vm.content.fields && typeof this.vm.response === 'object' && this.vm.response) {
			(this.vm.content.fields || []).forEach((f) => {
				if (!f || f.type === 'subheader') return;
				const id = f.id || f.key;
				if (!id) return;
				if (f.type === 'range' && (this.vm.response[id] === undefined || this.vm.response[id] === null || this.vm.response[id] === '')) {
					this.vm.response[id] = this.fieldMidValue(f);
				}
			});
		}

		const blocks: any[] = [];

		blocks.push({ type: 'header' });

		if (this.vm.assignmentNote) {
			blocks.push({ type: 'therapist_note', note: this.vm.assignmentNote });
		}

		if (!this.vm.isPreview) {
			blocks.push({ type: 'privacy_note' });
		}

		if (this.vm.kind === 'resource') {
			const format = (this.vm.item?.format || '').toString();
			if (format === 'psychoeducation') {
				blocks.push({ type: 'psychoeducation' });
				const reflections = (this.vm.content && this.vm.content.reflections) || [];
				if (Array.isArray(reflections) && reflections.length) {
					blocks.push({ type: 'reflections', questions: reflections });
				}
				if (!this.vm.isPreview) {
					blocks.push({ type: 'cta_complete', variant: 'psychoeducation' });
				} else {
					blocks.push({ type: 'preview_disabled_note' });
				}
			} else {
				// Dynamic form renderer: requires "fields" array in content.
				if (this.isDynamicFormContent(this.vm.content)) {
					blocks.push({ type: 'interactive' });
				} else {
					blocks.push({ type: 'unsupported_content' });
				}
				if (!this.vm.isPreview) {
					blocks.push({ type: 'cta_complete', variant: 'interactive' });
				} else {
					blocks.push({ type: 'preview_disabled_note' });
				}
			}
		}

		this.vm.blocks = blocks;
	}

	private collectResponseFromModel(): any {
		const out: any = {};
		const fields: any[] = (this.vm.content && this.vm.content.fields) || [];

		fields.forEach((f) => {
			if (!f || f.type === 'subheader') return;
			const id = f.id || f.key;
			if (!id) return;
			let v = this.vm.response[id];
			const type = (f?.type || '').toString();
			// Coerce numeric types; Angular inputs often bind numbers as strings.
			if ((type === 'range' || type === 'number') && v !== undefined && v !== null && v !== '') {
				const n = Number(v);
				v = isFinite(n) ? n : v;
			}
			if ((type === 'select' || type === 'chips') && v !== undefined && v !== null && v !== '') {
				v = this.mapChoiceValueToSnapshotOption(id, v);
			}
			if (v !== undefined) out[id] = v;
		});

		// reflections
		const reflections: any[] = (this.vm.content && this.vm.content.reflections) || [];
		if (Array.isArray(reflections)) {
			reflections.forEach((q, i) => {
				const key = 'reflection_' + i;
				if (this.vm.response[key] !== undefined) out[key] = this.vm.response[key];
			});
		}

		return out;
	}

	private mapChoiceValueToSnapshotOption(fieldId: string, value: any): any {
		if (value === undefined || value === null || value === '') return value;

		const snapshotFields = Array.isArray(this.vm.snapshotContent?.fields) ? this.vm.snapshotContent.fields : [];
		const currentFields = Array.isArray(this.vm.content?.fields) ? this.vm.content.fields : [];
		const snapshotField = this.findFieldById(snapshotFields, fieldId);
		if (!snapshotField) return value;

		const snapshotOptions = this.describeOptions(Array.isArray(snapshotField.options) ? snapshotField.options : [], this.currentLocale(), false);
		if (!snapshotOptions.length) return value;

		const valueString = value.toString();
		const directSnapshotMatch = snapshotOptions.find((option) => option.label === valueString || option.key === valueString);
		if (directSnapshotMatch) return directSnapshotMatch.label;

		const currentField = this.findFieldById(currentFields, fieldId);
		const currentOptions = this.describeOptions(Array.isArray(currentField?.options) ? currentField.options : [], this.currentLocale(), true);
		const currentIndex = currentOptions.findIndex((option) => option.label === valueString || option.key === valueString);
		if (currentIndex < 0 || currentIndex >= snapshotOptions.length) return valueString;

		return snapshotOptions[currentIndex].label;
	}

	submit() {
		// Client exercise view requires an assignment id to persist completion.
		if (this.vm.isPreview) return;
		if (this.vm.isReadOnly) return;
		if (!this.vm.assignmentId) {
			this.toast.showWithTranslation('Toolkit.Preview.NoAssignment');
			return;
		}

		const response = this.collectResponseFromModel();
		this.vm.submitting = true;
		this.toolkitService.submitMyCompletion(
			{ assignmentId: this.vm.assignmentId },
			{ response: response, clientLocale: null },
			() => {
				this.vm.submitting = false;
				this.toast.showWithTranslation('Toolkit.Preview.Completed');
			},
			() => {
				this.vm.submitting = false;
				this.toast.showWithTranslation('Toolkit.Preview.CompleteError');
			}
		);
	}

	close() {
		const returnTo = (this.route.snapshot.queryParamMap.get('return_to') || '').toString();
		this.angularRoutingHelperService.setPath(returnTo || '/toolkit', false, false);
	}

	private currentLocale(): string {
		return (this.dictionaryService.getCurrentLocale() || '').toString();
	}
}
